document.addEventListener("alpine:init", () => {
  Alpine.data("product", (options) => ({
    product: null,
    selectedOptions: [],
    selectedVariant: null,
    optionStatus: {},
    optionImages: {},
    swatchOptionIndices: [],
    deliveryDate: "",
    xplus: options.xplus,
    moneyFormat: options.moneyFormat,
    nutritionInfo: {},
    quantity: 1,

    init() {
      const productJsonString = this.$refs.productJson.textContent;
      this.product = JSON.parse(productJsonString);

      const initialVariant =
        this.product.variants.find((v) => v.id === options.selectedVariantId) ||
        this.product.variants[0];
        
      this.selectedVariant = initialVariant;
      this.selectedOptions = [...initialVariant.options];

      this.initializeOptions(options.swatchOptionNames);
      this.updateState();

      this.deliveryDate = this.getDeliveryDate();
      if (this.$refs.nutritionInfo) {
        const nutritionInfoString = this.$refs.nutritionInfo.textContent;
        this.nutritionInfo = JSON.parse(nutritionInfoString);
      }
      this.$watch("selectedVariant", (variant) => {
        if (!variant) return;
        this.updateMainImage();
        this.updateUrlWithVariantId();
      });
    },

    initializeOptions(swatchOptionNames) {
      if (!swatchOptionNames) swatchOptionNames = [];
      this.swatchOptionIndices = this.product.options
        .map((name, index) => (swatchOptionNames.includes(name) ? index : -1))
        .filter((index) => index !== -1);

      this.product.options.forEach((optionName, optionIndex) => {
        this.optionStatus[optionIndex] = {};
        if (this.swatchOptionIndices.includes(optionIndex)) {
          this.optionImages[optionIndex] = {};
        }
        const valuesForOption = [
          ...new Set(
            this.product.variants.map((variant) => variant.options[optionIndex])
          ),
        ];

        valuesForOption.forEach((value) => {
          if (value) {
            this.optionStatus[optionIndex][value] = {
              hidden: false,
              disabled: false,
            };
            if (this.swatchOptionIndices.includes(optionIndex)) {
              const imageVariant = this.product.variants.find(
                (v) => v.options[optionIndex] === value && v.featured_image
              );
              this.optionImages[optionIndex][value] = imageVariant
                ? imageVariant.featured_image.src
                : "";
            }
          }
        });
      });
    },

    optionChanged(optionIndex, value) {
      this.selectedOptions[optionIndex] = value;
      for (let i = optionIndex + 1; i < this.selectedOptions.length; i++) {
        this.selectedOptions[i] = null;
      }
      this.updateState();
    },

    updateState() {
      let stateChanged;
      do {
        this.updateOptionStatus();
        this.updateSwatchImages(); // Update images based on new status
        stateChanged = this.autoSelectNextAvailable();
      } while (stateChanged);

      this.updateSelectedVariant();
    },

    updateSwatchImages() {
      if (this.swatchOptionIndices.length === 0) return;

      this.swatchOptionIndices.forEach((swatchIndex) => {
        const swatchValues = Object.keys(this.optionStatus[swatchIndex] || {});
        swatchValues.forEach((value) => {
          const potentialSelection = [...this.selectedOptions];
          potentialSelection[swatchIndex] = value;

          const bestVariant =
            this.product.variants.find((variant) => {
              if (!variant.featured_image) return false;
              let matches = true;
              for (let i = 0; i < potentialSelection.length; i++) {
                if (
                  potentialSelection[i] !== null &&
                  potentialSelection[i] !== variant.options[i]
                ) {
                  matches = false;
                  break;
                }
              }
              return matches;
            }) ||
            this.product.variants.find((variant) => {
              if (
                !variant.featured_image ||
                variant.options[swatchIndex] !== value
              )
                return false;
              let matches = true;
              for (let i = 0; i < this.selectedOptions.length; i++) {
                if (i === swatchIndex) continue;
                if (
                  this.selectedOptions[i] !== null &&
                  this.selectedOptions[i] !== variant.options[i]
                ) {
                  matches = false;
                  break;
                }
              }
              return matches;
            }) ||
            this.product.variants.find(
              (v) => v.options[swatchIndex] === value && v.featured_image
            );

          if (bestVariant) {
            this.optionImages[swatchIndex][value] =
              bestVariant.featured_image.src;
          }
        });
      });
    },

    getSwatchImage(optionIndex, value) {
      const imageUrl = this.optionImages[optionIndex]
        ? this.optionImages[optionIndex][value]
        : null;
      if (!imageUrl)
        return "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="; // Transparent pixel
      // Shopify's `replace` trick for getting a different image size from the URL
      return imageUrl.replace(
        /(\.jpg|\.png|\.jpeg|\.gif)\?/,
        "_100x100_crop_center$1?"
      );
    },

    updateOptionStatus() {
      this.product.options.forEach((optionName, optionIndex) => {
        const valuesForOption = [
          ...new Set(
            this.product.variants.map((variant) => variant.options[optionIndex])
          ),
        ];
        valuesForOption.forEach((value) => {
          if (!value) return;

          // Find variants that match the current value AND all PRECEDING option selections.
          const potentialMatches = this.product.variants.filter((variant) => {
            for (let i = 0; i < optionIndex; i++) {
              if (
                this.selectedOptions[i] !== null &&
                variant.options[i] !== this.selectedOptions[i]
              ) {
                return false;
              }
            }
            return variant.options[optionIndex] === value;
          });

          // If no such variant exists, this option is impossible.
          if (potentialMatches.length === 0) {
            this.optionStatus[optionIndex][value].hidden = true;
            this.optionStatus[optionIndex][value].disabled = true;
          } else {
            // Otherwise, it's possible. Check if any of the potential variants are available.
            this.optionStatus[optionIndex][value].hidden = false;
            const isAvailable = potentialMatches.some(
              (variant) => variant.available
            );
            this.optionStatus[optionIndex][value].disabled = !isAvailable;
          }
        });
      });
    },

    autoSelectNextAvailable() {
      let changed = false;
      for (let i = 0; i < this.selectedOptions.length; i++) {
        if (
          this.selectedOptions[i] === null ||
          (this.optionStatus[i] &&
            this.optionStatus[i][this.selectedOptions[i]] &&
            this.optionStatus[i][this.selectedOptions[i]].hidden)
        ) {
          const valuesForOption = [
            ...new Set(
              this.product.variants.map((variant) => variant.options[i])
            ),
          ];
          const firstAvailable = valuesForOption.find((value) => {
            return (
              value &&
              this.optionStatus[i][value] &&
              !this.optionStatus[i][value].hidden
            );
          });
          const newValue = firstAvailable || null;
          if (this.selectedOptions[i] !== newValue) {
            this.selectedOptions[i] = newValue;
            changed = true;
          }
        }
      }
      return changed;
    },

    updateSelectedVariant() {
      if (this.selectedOptions.every((opt) => opt !== null)) {
        const foundVariant = this.product.variants.find((variant) => {
          return variant.options.every(
            (opt, index) => opt === this.selectedOptions[index]
          );
        });
        this.selectedVariant = foundVariant || null;
      } else {
        this.selectedVariant = null;
      }

      window.dispatchEvent(new CustomEvent("variant:updated"));
    },

    // Your original methods
    submitForm(form) {
      const formData = new FormData(form);
      fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then((response) => response.json())
        .then((data) => {
          document.dispatchEvent(new CustomEvent("cart:drawer:open"));
        })
        .catch((error) => console.error("Error:", error));
    },

    updateMainImage() {
      if (this.selectedVariant && this.selectedVariant.featured_image) {
        const mainImageContainer = document.querySelector(".xp-gallery");
        if (!mainImageContainer) return;
        const newImg = document.createElement("img");
        newImg.src = this.selectedVariant.featured_image.src;
        newImg.alt = this.selectedVariant.featured_image.alt;
        newImg.className = "xp-gallery__image";
        mainImageContainer.innerHTML = "";
        mainImageContainer.appendChild(newImg);
      }
    },

    updateUrlWithVariantId() {
      if (this.selectedVariant && this.selectedVariant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", this.selectedVariant.id);
        window.history.replaceState({}, "", url.toString());
      }
    },

    formatMoney(cents) {
      if (typeof cents !== "number") return "";
      let format = this.moneyFormat;
      let amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    },

    getDeliveryDate(daysToAdd = 4) {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + daysToAdd);
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 2);
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      if (startDate.getMonth() !== endDate.getMonth()) {
        return `${startDate.getDate()}${getOrdinalSuffix(
          startDate.getDate()
        )} ${
          months[startDate.getMonth()]
        } - ${endDate.getDate()}${getOrdinalSuffix(endDate.getDate())} ${
          months[endDate.getMonth()]
        }`;
      } else {
        return `${startDate.getDate()}${getOrdinalSuffix(
          startDate.getDate()
        )} - ${endDate.getDate()}${getOrdinalSuffix(endDate.getDate())} ${
          months[endDate.getMonth()]
        }`;
      }
    },
  }));
});
